import React, { useRef } from 'react'
import Flex from '../Flex'
import Text from '../Text'
import { IInput, InputProps } from './Input.types'
import InputNumber from './InputNumber'
import InputPassword from './InputPassword'
import InputRadio from './InputRadio'
import { InputWrapper, PlainInput, StyledInput } from './styled'
import TextArea from './TextArea'

const forwardRef = React.forwardRef<HTMLInputElement, InputProps>(
  (
    {
      prefix,
      sufix,
      label,
      hint,
      isError,
      error,
      maxLength,
      value,
      disabled,
      isPlain = true,
      noBorder = false,
      size = 'regular',
      showCount = false,
      onChange = () => null,
      ...restProps
    },
    ref,
  ): JSX.Element => {
    const inputRef =
      (ref as React.MutableRefObject<HTMLInputElement>) || useRef<HTMLInputElement>(null)

    // // hard to reproduce
    // /* istanbul ignore next */
    const onClickInput = () => {
      if (inputRef.current) inputRef.current.focus()
    }

    const renderErrorText = () => {
      if (isError && error) {
        if (showCount && maxLength) {
          return (
            <Flex width='100%' justifyContent='space-between'>
              <Text variant='caption' color='red50' aria-label='input-error'>
                {error}
              </Text>
              <Text
                variant='caption'
                color='red50'
                aria-label='input-count'
              >{`${value.length}/${maxLength}`}</Text>
            </Flex>
          )
        }
        return (
          <Text variant='caption' color='red50' aria-label='input-error'>
            {error}
          </Text>
        )
      }
      if (hint) {
        if (showCount && maxLength) {
          return (
            <Flex width='100%' justifyContent='space-between'>
              <Text variant='caption' color='black50' aria-label='input-hint'>
                {hint}
              </Text>
              <Text
                variant='caption'
                color='black50'
                aria-label='input-count'
              >{`${value.length}/${maxLength}`}</Text>
            </Flex>
          )
        }
        return (
          <Text variant='caption' color='black50' aria-label='input-hint'>
            {hint}
          </Text>
        )
      }
      if (showCount && maxLength) {
        return (
          <Flex width='100%' justifyContent='flex-end'>
            <Text
              color='black50'
              variant='caption'
              aria-label='input-count'
            >{`${value.length}/${maxLength}`}</Text>
          </Flex>
        )
      }
    }

    const handleOnChange = (event: React.FormEvent<HTMLInputElement>) => {
      if (maxLength) {
        if (event.currentTarget.value.length <= maxLength) {
          return onChange(event)
        }
        const value = event.currentTarget.value
        event.currentTarget.value = value.substring(0, maxLength)
        return onChange(event)
      } else {
        return onChange(event)
      }
    }

    return (
      <Flex flexDirection='column' rowGap='8px' width='100%'>
        {label && (
          <Text variant='caption' weight='bold' color='black50'>
            {label}
          </Text>
        )}
        {prefix || sufix ? (
          <InputWrapper
            aria-label='input-wrapper'
            size={size}
            isError={isError}
            onClick={onClickInput}
            disabled={disabled}
            noBorder={noBorder}
            style={{
              backgroundColor: restProps?.style?.backgroundColor ?? 'none',
            }}
          >
            {prefix}
            <PlainInput
              aria-label='plain-input'
              isPlain={isPlain}
              ref={inputRef}
              size={size}
              isError={isError}
              value={value}
              onChange={handleOnChange}
              disabled={disabled}
              // onPaste={(e) => console.log('called', e.target)}
              {...restProps}
            />
            {sufix}
          </InputWrapper>
        ) : (
          <StyledInput
            onChange={handleOnChange}
            size={size}
            value={value}
            ref={inputRef}
            isError={isError}
            disabled={disabled}
            noBorder={noBorder}
            // onPaste={(e) => console.log('called', e.target)}
            {...restProps}
          />
        )}
        {renderErrorText()}
      </Flex>
    )
  },
)

export const Input = {
  ...forwardRef,
  Number: InputNumber,
  Radio: InputRadio,
  Password: InputPassword,
  TextArea: TextArea,
} as IInput

// export const Input = React.forwardRef(
//   (props: InputProps, ref: React.Ref<HTMLInputElement>): JSX.Element => {
//     return <_Input {...props} forwardedRef={ref} />
//   },
// )

// Input.Number = InputNumber
// Input.Radio = InputRadio
// Input.Password = InputPassword
// Input.TextArea = TextArea

export default Input
